import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, useTheme, Box, Paper, Typography, IconButton, Table, TableBody, TableRow, TableCell, TableContainer, CircularProgress, Stepper, Step, StepLabel, Avatar, Accordion, AccordionSummary, AccordionDetails, Button } from '@material-ui/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close'; 
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useConfirm } from "material-ui-confirm";
import { ORD_TYPES, ORD_STATES } from '../models/ordConfig';
import QeeSlideDialog from './qeeSlideDialog';
import QeeSingleFieldEditor from './qeeSingleFieldEditor';
import QeeArticleForm from './qeeArticleForm';
import OfferForm from './offerForm';
import OfferPreview from './offerPreview';
import ContractForm from './contractForm';
import ContractPreview from './contractPreview';
import QeeHintBox from './qeeHintBox';
import { fetchOrd, ordSave, ordQuitRequest, ordPutOffer, ordAcceptOffer, ordPutContract, ordQuitContract, ordRejectRequest, ordRejectOffer } from '../api/service';
import { useAuth } from '../context/provider';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
    },
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        maxWidth: 1000,
    },
    subform: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(0),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
    },	
    tableCell: {
        padding: theme.spacing(2),
    },
    fieldLabel: {
        fontWeight: 600,
        marginRight: theme.spacing(1),
    },
    iconButton: {
        padding: theme.spacing(1),
    },
    progressContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
    },
    stepLabel: {
        color: theme.palette.text.secondary,
        '&.MuiStepLabel-active': {
            color: theme.palette.primary.main,
            fontWeight: 'bold',
        },
    },
    stepNumber: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        width: theme.spacing(4),
        height: theme.spacing(4),
        fontSize: '1rem',
        marginRight: theme.spacing(1),
    },
    stepNumberGrey: {
        backgroundColor: theme.palette.grey[400],
        color: theme.palette.common.white,
        width: theme.spacing(4),
        height: theme.spacing(4),
        fontSize: '1rem',
        marginRight: theme.spacing(1),
    },
    addIcon: {
        backgroundColor: theme.palette.primary.main, 
        color: theme.palette.common.white,
        cursor: 'pointer',
    },  
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    avatarButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: theme.spacing(20),
        height: theme.spacing(5),
        borderRadius: theme.shape.borderRadius,
        margin: theme.spacing(1),
		padding: theme.spacing(2),
    },
    positiveButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    negativeButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.common.white,
    },
    // Adjusted styles for smaller screens
    tableRow: {
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.divider}`, // Row separator
        },
    },
    tableCellSmall: {
        display: 'table-cell', // Default to table-cell behavior for large screens
        paddingBottom: theme.spacing(1),
        [theme.breakpoints.down('sm')]: { 
            display: 'block',
            width: '100%',  // Ensure full width on small screens
            padding: theme.spacing(1, 0),
            borderBottom: 'none',  // Remove vertical cell separators on small screens
        },
    }
}));



const OrdForm = ({ ordId, sac }) => {
		
    const classes = useStyles();
    const theme = useTheme();
	const confirm = useConfirm();	
	
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));	
	
    const { setObjEdit, setOpenQeeDialog } = useAuth();
    const [ordData, setOrdData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [changesDialog, setChangesDialog] = useState({});
    const [selectedArtId, setSelectedArtId] = useState(null);
	const [hasErrorsDlgOffer, setHasErrorsDlgOffer] = useState( true);				
	const [changesDlgOffer, setChangesDlgOffer] = useState( true);					
	const [hasErrorsDlgContract, setHasErrorsDlgContract] = useState( true);				
	const [changesDlgContract, setChangesDlgContract] = useState( true);						
    
    const requestRef = useRef(null);
    const offerRef = useRef(null);
    const contractRef = useRef(null);

    // Determine the initial expanded panel based on ord_state
    const getInitialExpandedPanel = (ordState) => {
        if (ordState >= 300) return 'contract';
        if (ordState >= 200) return 'offer';
        return 'request';
    };

    const [expanded, setExpanded] = useState('request');

    useEffect(() => {
		
		console.log( 'Role isAdmin', sac?.sac_is_admin);
		console.log( 'Role isSender', sac?.sac_is_sender);
		console.log( 'Role isReceiver', sac?.sac_is_receiver);
		
        const fetchOrderData = async () => {
            try {
                const data = await fetchOrd(ordId);
                setOrdData( data);
				console.log( 'ordData', data);
                setExpanded(getInitialExpandedPanel(data.ord_state)); // Set expanded panel based on ord_state
            } catch (error) {
                console.error('Error fetching order data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrderData();
    }, [ordId]);

    const handleOpenDialog = (dialogId) => {
        setOpenQeeDialog({ open: true, id: dialogId });
    };

    const handleOpenArticleDialog = (artId) => {
        setSelectedArtId(artId);
        handleOpenDialog("dlgArticle");
    };

    const handleChangesDialog = (changes) => {
        setChangesDialog(changes);
    };

    const handleSave = async () => {
        try {
            setChangesDialog({});
        } catch (error) {
            console.error('Error saving order:', error);
        }
    };
	
	const handleQuitRequest = async () => {
		console.log('handleQuitRequest');
		try {
			confirm({
				title: "Anfrage stornieren",
				description: "Die Anfrage wird storniert und alle Beteiligte informiert. . Möchtest du fortfahren?",
				confirmationText: "Stornierung starten",
				cancellationText: "Abbrechen",
				confirmationButtonProps: { color: "error", variant: "contained", style: { textTransform: 'none' } },
				cancellationButtonProps: { color: "success", style: { textTransform: 'none' } }
			})
			.then(async () => {
				await ordQuitRequest(ordId);
				const updatedData = await fetchOrd(ordId);
				setOrdData(updatedData);
			})
			.catch(() => {
				// Abgebrochen oder Fehler im Dialog
			});

		} catch (error) {
			console.error('handleQuitRequest ERR:', error);
		}
	};

    const handleQuitContract = async () => {
		console.log( 'handleQuitContract');
        try {
			confirm({
				title: "Vertrag stornieren",
				description: "Es wird versucht, den laufenden Vertrag zu stornieren. Wenn die vertraglichen Bedingungen eine Stornierung zulassen, wird der Vertrag aufgehoben. Möchtest du den Vorgang fortsetzen?",
				confirmationText: "Stornierung starten",
				cancellationText: "Abbrechen",
				confirmationButtonProps: { color: "error", variant: "contained", style: { textTransform: 'none' } },
				cancellationButtonProps: { color: "success", style: { textTransform: 'none' } }
			})
			.then(async () => {						
				await ordQuitContract(ordId);
				const updatedData = await fetchOrd(ordId);
				setOrdData(updatedData);
			})
			.catch(() => {
				// Abgebrochen oder Fehler im Dialog
			});
				
        } catch (error) {
            console.error( 'handleQuitContract ERR:', error);
        }
    };	

    const handleRejectRequest = async () => {
        console.log( 'handleRejectRequest');
        try {		
			confirm({
				title: "Anfrage stornieren",
				description: "Die Anfrage wird abgelehnt und alle Beteiligte informiert. Möchtest du fortfahren?",
				confirmationText: "Stornierung starten",
				cancellationText: "Abbrechen",
				confirmationButtonProps: { color: "error", variant: "contained", style: { textTransform: 'none' } },
				cancellationButtonProps: { color: "success", style: { textTransform: 'none' } }
			})
			.then(async () => {
				await ordRejectRequest(ordId);
				const updatedData = await fetchOrd(ordId);
				setOrdData(updatedData);
			})
			.catch(() => {
				// Abgebrochen oder Fehler im Dialog
			});
        } catch (error) {
            console.error( 'handleRejectRequest ERR:', error);
        }		
    };

    const handleAcceptOffer = async () => {
		console.log('handleAcceptOffer');
		try {
			await ordAcceptOffer(ordId); 
			const updatedData = await fetchOrd(ordId);
			setOrdData(updatedData);
		} catch (error) {
			console.error('handleAcceptOffer ERR:', error);
		}
    };

	const handleRejectOffer = async () => {
		console.log('handleRejectOffer');
		try {
			await ordRejectOffer(ordId); 
			const updatedData = await fetchOrd(ordId);
			setOrdData(updatedData);
		} catch (error) {
			console.error('handleRejectOffer ERR:', error);
		}
	};

    const scrollToSection = (ref) => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    if (isLoading) {
        return (
            <div className={classes.progressContainer}>
                <CircularProgress />
            </div>
        );
    }

    if (!ordData) {
        return (
            <div className={classes.progressContainer}>
                <Typography variant="h6">Bestellung nicht gefunden.</Typography>
            </div>
        );
    }

	/*
    //const steps = Object.values(ORD_STATES[ordData.ord_type] || {});
	const steps = Object.values(ORD_STATES[ordData.ord_type] || ORD_STATES['default']).slice(0, 3);
	console.log( '>>> STEPS: ', steps)
    const activeStep = steps.findIndex(step => step === ORD_STATES[ordData.ord_type][ordData.ord_state]);
	*/
	
	// Fallback to 'default' if the specific ord_type is not found
	const steps = Object.values(ORD_STATES[ordData.ord_type] || ORD_STATES['default']).slice(0, 3);
	console.log('>>> STEPS: ', steps);

	// Safely get the ord_state for the ord_type or default to 'default' states
	const ordStatesForType = ORD_STATES[ordData.ord_type] || ORD_STATES['default'];

	// Safely get the ord_state (or fallback if it doesn't exist)
	const activeStep = steps.findIndex(step => step === ordStatesForType[ordData.ord_state]);
	console.log('>>> ACTIVE STEP: ', activeStep);	
	

    const configRequest = [
        {
            label: 'Artikel:',
            sublabel: 'Angefragter Artikel vom Shop',
            item_1: `${ordData.art_name} (Artikel-Nr. ${ordData.art_id})`,
            cond: ordData.art_id && ordData.art_id !== '0',
            btnOpenDialog: true
        },
        {
            label: 'Kategorie:',
            sublabel: 'Kategorie der Bestellung',
            item_1: ORD_TYPES[ordData.ord_type],
        },
    ];

    const configOffer = [
    ];

    const configContract = [
        {
            label: 'Auftragsnummer:',
            sublabel: 'Nummer des Auftrags',
            item_1: ordData.contractNumber || 'N/A',
        },
    ];
    
	const getStepIcon = (index) => {
		console.log( 'getStepIcon', index, ordData.ord_state);
		if (ordData.ord_state === 10100 && index === 0) {
			return <CloseIcon style={{ color: theme.palette.error.main }} />;
		}
		if (ordData.ord_state === 10200 && index === 1) {
			return <CloseIcon style={{ color: theme.palette.error.main }} />;
		}
		if (ordData.ord_state >= (index + 1) * 100) {
			if (index < activeStep) {
				return <CheckIcon  style={{ backgroundColor: theme.palette.primary.main, color: 'white' }} />;
			}
			return `${index + 1}`;
		}
		//if (ordData.ord_state === index * 100) {
		//	return <AddIcon className={classes.addIcon} />;
		//}
		return `${index + 1}`;
	};

	const getAvatarClass = (index) => {
		console.log( '--> getAvatarClass', index, ordData.ord_state);
		if ((ordData.ord_state === 10100 && index >= 1) || 
			(ordData.ord_state === 10200 && index >= 2)) {
			return classes.stepNumberGrey; // Use grey background but will be overridden by red icon
			console.log( '<-- getAvatarClass', 'grey');
		}
		if (ordData.ord_state >= index * 100) {
			console.log( '<-- getAvatarClass', 'stepNumber');
			return classes.stepNumber;
		}
		console.log( '<-- getAvatarClass', 'stepNumberGrey');
		return classes.stepNumberGrey;
	};
	
	const renderSectionHeader = (index, title, ref, panelName) => {
		console.log('--> renderSectionHeader', index, ordData.ord_state, title);

		// Skip rendering if the section is rejected or if state is 300 and there's no contract
		if ((ordData.ord_state === 10100 && index >= 1) ||
			(ordData.ord_state === 10200 && index >= 2) ||
			(ordData.ord_state === 300 && index === 2 && !ordData.contract && !sac?.sac_is_receiver)) {
			console.log('<-- renderSectionHeader: null');
			return null;
		}

		return (
			ordData.ord_state > (index * 100) && (
				<Accordion style={{maxWidth:1000}}
					expanded={expanded === panelName}
					onChange={handleAccordionChange(panelName)}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ width: '100%' }}>
						<Box className={classes.titleContainer} ref={ref} style={{ width: '100%' }}>
							<Avatar className={getAvatarClass(index)}>
								{getStepIcon(index)}
							</Avatar>
							<Typography variant="h5" gutterBottom>
								{title}
							</Typography>
						</Box>
					</AccordionSummary>
					<AccordionDetails style={{ width: '100%' }}>
						<Box style={{ width: '100%' }}>
							<Box display="flex" flexDirection="row" mt={2}>
								{renderSectionHints(index)}
							</Box>
							{index === 0 && renderTableSection(configRequest, true)}
							{index === 1 && renderTableSection(configOffer, false, true)}
							{index === 2 && renderTableSection(configContract, false, false, true)}
							<Box display="flex" flexDirection="row" mt={2}>
								{renderSectionButtons(index)}
							</Box>
						</Box>
					</AccordionDetails>
				</Accordion>
			)
		);
	};

	const renderSectionButtons = (index) => {
		// Ensure that buttons are shown for the relevant section and state
		if (ordData.ord_state > (index + 1) * 100 && !(index === 2 && ordData.ord_state === 300 && sac?.sac_is_receiver)) {
			console.log('renderSectionButtons no buttons', ordData.ord_state, (index + 1) * 100);
			return null;
		}

		switch (index) {
			case 0:
				return (
					<>
						{sac?.sac_is_sender &&
							<>
								<Button
									className={`${classes.avatarButton} ${classes.positiveButton}`}
									onClick={handleQuitRequest}
								>
									Anfrage stornieren
								</Button>
							</>
						}
						{sac?.sac_is_receiver &&
							<>
								<Button
									className={`${classes.avatarButton} ${classes.positiveButton}`}
									onClick={() => handleOpenDialog("dlgOffer")}
								>
									Angebot erstellen
								</Button>
								<Button
									className={`${classes.avatarButton} ${classes.negativeButton}`}
									onClick={handleRejectRequest}
								>
									Anfrage ablehnen
								</Button>
							</>
						}
					</>
				);
			case 1:
				return (
					<>
						{sac?.sac_is_receiver &&
							<>
								<Button
									className={`${classes.avatarButton} ${classes.positiveButton}`}
									onClick={() => handleOpenDialog("dlgContract")}
								>
									Vertrag erstellen
								</Button>
							</>
						}
						{sac?.sac_is_sender &&
							<>
								<Button
									className={`${classes.avatarButton} ${classes.positiveButton}`}
									onClick={handleAcceptOffer}
								>
									Angebot annehmen
								</Button>
								<Button
									className={`${classes.avatarButton} ${classes.negativeButton}`}
									onClick={handleRejectOffer}
								>
									Angebot ablehnen
								</Button>
							</>
						}
					</>
				);
			case 2:
				if (!ordData.contract && sac?.sac_is_receiver) {
					return (
						<>
							<Button
								className={`${classes.avatarButton} ${classes.positiveButton}`}
								onClick={() => handleOpenDialog("dlgContract")}
							>
								Vertrag erstellen
							</Button>
						</>
					);
				//} else if (ordData.contract && sac?.sac_is_sender) {
				} else if (ordData.contract) {	
					return (
						<>
							<Button
								className={`${classes.avatarButton} ${classes.negativeButton}`}
								onClick={handleQuitContract}
							>
								Vertrag kündigen
							</Button>
						</>
					);
				}
				return null;
			default:
				return null;
		}
	};

	const renderSectionHints = (index) => {
		const hintConfigs = [
			// Cancelations
			{ state: 10100, type: "error", senderText: "Deine Anfrage wurde storniert. Weitere Schritte sind nicht möglich.", receiverText: "Die Anfrage wurde storniert. Du kannst keine Angebote mehr erstellen." },
			{ state: 10200, type: "error", senderText: "Das Angebot wurde abgelehnt. Bitte überprüfe die Details und nimm Kontakt mit dem Kunden auf.", receiverText: "Das Angebot wurde vom Anfrager abgelehnt." },
			{ state: 10300, type: "error", senderText: "Der Vertrag wurde gekündigt. Es sind keine weiteren Aktionen erforderlich.", receiverText: "Der Vertrag wurde gekündigt. Du musst keine weiteren Schritte unternehmen." },

			// Success messages
			{ state: 100, type: "success", senderText: "Deine Anfrage wurde erfolgreich eingereicht. Wir informieren dich über den weiteren Verlauf.", receiverText: null },
			{ state: 200, type: "success", senderText: null, receiverText: "Dein Angebot wurde erfolgreich erstellt. Warte auf die Bestätigung des Anfragers." },
			{ state: 300, type: "success", senderText: "Der Vertrag wurde erfolgreich abgeschlossen. Die Zusammenarbeit kann beginnen.", receiverText: "Der Vertrag wurde erfolgreich abgeschlossen. Die Arbeit kann beginnen.", contractRequired: true },

			// Info (imperative) messages
			{ state: 100, type: "info", senderText: null, receiverText: "Eine neue Anfrage ist eingegangen. Du kannst jetzt ein Angebot erstellen." },
			{ state: 200, type: "info", senderText: "Das Angebot liegt vor. Bestätige es, um den Vertrag abzuschließen.", receiverText: null },

			// Special handling for state 300 without contract
			{ state: 300, type: "info", senderText: "Der Vertrag wird erstellt, wir benachrichtigen dich wenn es soweit ist.", receiverText: "Das Angebot wurde akzeptiert. Erstelle jetzt den Vertrag, um den Auftrag abzuschließen.", contractRequired: false },
		];

		let selectedHint = null;
		let hintType = null;

		// Collect all matching hints to avoid stopping at the first match
		const matchingHints = hintConfigs.filter(hint => hint.state === ordData.ord_state);

		// Special case for state 300 without contract
		if (ordData.ord_state === 300 && !ordData.contract) {
			selectedHint = matchingHints.find(hint => hint.contractRequired === false);
		} else {
			// Find the first hint where the relevant text (sender or receiver) is not null
			selectedHint = matchingHints.find(hint => {
				if (sac?.sac_is_sender) {
					return hint.senderText !== null;
				} else if (sac?.sac_is_receiver) {
					return hint.receiverText !== null;
				}
				return false;
			});
		}

		console.log('>>> selectedHint', selectedHint);

		if (!selectedHint) {
			return sac?.sac_is_admin ? <QeeHintBox type="info" text="DEBUG: Admin-Modus aktiv. Alle Hinweise sind sichtbar." style={{ marginBottom: 20 }} /> : null;
		}

		// Determine the hint text based on whether the sender or receiver is viewing
		let hintText = sac?.sac_is_admin
			? `DEBUG: Admin-Modus aktiv. [${selectedHint.state}] ${sac?.sac_is_sender ? selectedHint.senderText : selectedHint.receiverText}`
			: sac?.sac_is_sender
			? selectedHint.senderText
			: sac?.sac_is_receiver
			? selectedHint.receiverText
			: null;

		hintType = selectedHint.type;

		// If the hint text is null for the sender but there's an imperative message, use that
		if (!hintText && sac?.sac_is_sender) {
			const imperativeHint = hintConfigs.find(hint => hint.state === ordData.ord_state && hint.type === "info");
			if (imperativeHint && !selectedHint.senderText) {
				hintText = imperativeHint.senderText;
				hintType = imperativeHint.type; // Use the type from the imperative hint
			} else {
				hintText = selectedHint.senderText;
				hintType = selectedHint.type;
			}
		}

		if (!hintText) return null;

		switch (index) {
			case 0:
				if (ordData.ord_state === 10100 || ordData.ord_state === 100) {
					return <QeeHintBox type={hintType} text={hintText} style={{ marginBottom: 20 }} />;
				}
				break;
			case 1:
				if (ordData.ord_state === 10200 || ordData.ord_state === 200) {
					return <QeeHintBox type={hintType} text={hintText} style={{ marginBottom: 20 }} />;
				}
				break;
			case 2:
				if (ordData.ord_state === 10300 || (ordData.ord_state === 300 && ordData.contract)) {
					return <QeeHintBox type={hintType} text={hintText} style={{ marginBottom: 20 }} />;
				}
				if (ordData.ord_state === 300 && !ordData.contract && sac?.sac_is_receiver) {
					return <QeeHintBox type={hintType} text={hintText} style={{ marginBottom: 20 }} />;
				}
				break;
			default:
				return null;
		}
	};

	const renderTableSection = (config, includeLeadDetails = false, includeOfferDetails = false, includeContractDetails = false) => (
		<Box className={classes.subform}>
		<Table>
			<TableBody>
				{config.map((item, index) => (
					item.cond !== false && (
						<TableRow key={index} className={classes.tableRow}>
							<TableCell component="th" scope="row" className={classes.tableCellSmall}>
								<Box>
									<Typography variant="lbl" className={classes.fieldLabel} display="block">
										{item.label}
									</Typography>
									{item.sublabel && (
										<Typography variant="caption" color="textSecondary" display="block">
											{item.sublabel}
										</Typography>
									)}
								</Box>
							</TableCell>
							<TableCell className={classes.tableCellSmall}>
								<Typography variant="body2">
									{item.item_1}
								</Typography>
							</TableCell>
							<TableCell className={classes.tableCellSmall}>
								{item.btnOpenDialog && (
									<IconButton onClick={() => handleOpenArticleDialog(ordData.art_id)} className={classes.iconButton}>
										<MoreHorizIcon />
									</IconButton>
								)}
							</TableCell>
						</TableRow>
					)
				))}
				
				{/* Conditionally Render offer preview */}
				{includeOfferDetails && (				
					<TableRow>				
					<TableCell colSpan={3} className={classes.tableCellSmall}>
                        <OfferPreview ord={ordData} sac={sac} />
                    </TableCell>
					</TableRow>
				)}	
								
				{/* Conditionally Render Lead Details Table */}
				{includeLeadDetails && ordData.leadDetails && Object.keys(ordData.leadDetails).length > 0 && (
					Object.entries(ordData.leadDetails).map(([key, value], index) => (
						<TableRow key={index} className={classes.tableRow}>
							<TableCell component="th" scope="row" className={classes.tableCellSmall}>
								<Typography variant="body2" className={classes.fieldLabel}>
									{key}
								</Typography>
							</TableCell>
							<TableCell className={classes.tableCellSmall}>
								<Typography variant="body2">
									{String(value)}
								</Typography>
							</TableCell>
							<TableCell className={classes.tableCellSmall} />
						</TableRow>
					))
				)}
				
				{/* Conditionally Render contract preview */}
				{includeContractDetails && (				
					<TableRow>				
					<TableCell colSpan={3} className={classes.tableCellSmall}>
                        <ContractPreview ord={ordData} sac={sac} />
                    </TableCell>
					</TableRow>
				)}					
				
				
			</TableBody>
		</Table>
		</Box>
	);


	//
	// --- offer dialog
	//
	const handleHasErrorsDlgOffer = ( hasErrors) => {
		console.log( 'handleHasErrorsDlgOffer hasErrors', hasErrors);
		setHasErrorsDlgOffer( hasErrors);
	}					
	const handleChangesDlgOffer = async ( changes) => {
		console.log( 'handleChangesDlgOffer results', changes);

		setChangesDlgOffer( changes);		
	}								
	const handleSubmitDlgOffer = async () => {
		console.log('handleSubmitDlgOffer SUBMITTING >>>>');

		// Extend changesDlgOffer with the subobject offer
		const extendedChangesDlgOffer = {
			//...changesDlgOffer,
			offer: { ...changesDlgOffer },
			ord_state: 200
		};

		try {
			await ordPutOffer(ordId, extendedChangesDlgOffer);
			const updatedData = await fetchOrd(ordId);
			setOrdData(updatedData);

			// Close the dialog after successful submission
			setOpenQeeDialog({ open: false, id: 'dlgOffer' });
		} catch (error) {
			console.error('handleSubmitDlgOffer ERR:', error);
			// Handle error as needed
		}
	};


	//
	// --- contract dialog
	//
	const handleHasErrorsDlgContract = ( hasErrors) => {
		console.log( 'handleHasErrorsDlgContract hasErrors', hasErrors);
		setHasErrorsDlgContract( hasErrors);
	}					
	const handleChangesDlgContract = async ( changes) => {
		console.log( 'handleChangesDlgContract results', changes);

		setChangesDlgContract( changes);		
	}								
	const handleSubmitDlgContract = async () => {
		console.log('handleSubmitDlgContract SUBMITTING >>>>');

		// Extend changesDlgContract with the subobject offer
		const extendedChangesDlgContract = {
			//...changesDlgContract,
			contract: { ...changesDlgContract },
			ord_state: 300
		};

		try {
			await ordPutContract(ordId, extendedChangesDlgContract);
			const updatedData = await fetchOrd(ordId);
			setOrdData(updatedData);

			// Close the dialog after successful submission
			setOpenQeeDialog({ open: false, id: 'dlgContract' });
		} catch (error) {
			console.error('handleSubmitDlgContract ERR:', error);
			// Handle error as needed
		}
	};

    return (
        <div className={classes.root}>
            {/* Horizontal Stepper for Bestellstatus */}
            <Paper className={classes.paper}>
				<Stepper 
					activeStep={activeStep} 
					alternativeLabel={!isSmallScreen} 
					orientation={isSmallScreen ? 'vertical' : 'horizontal'}
				>
                    {steps.map((label, index) => (
                        <Step
                            key={index}
                            onClick={() => {
                                if (index === 0 && ordData.ord_state >= 0) scrollToSection(requestRef);								
                                if (index === 1 && ordData.ord_state >= 100) scrollToSection(offerRef);
                                if (index === 2 && ordData.ord_state >= 200) scrollToSection(contractRef);
                            }}
                        >
                            <StepLabel
                                icon={getStepIcon(index)} // Use the icon logic for the stepper
                                classes={{ label: classes.stepLabel }}
                                disabled={ordData.ord_state < (index + 1) * 100}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            {/* Request Section */}
            {renderSectionHeader(0, 'Anfrage', requestRef, 'request')}

            {/* Offer Section */}
            {renderSectionHeader(1, 'Angebot', offerRef, 'offer')}			

            {/* Contract Section */}
            {renderSectionHeader(2, 'Vertrag', contractRef, 'contract')}			

            {/* Dialogs for editing fields */}
            <QeeSlideDialog
                id="dlgArticleName"
                title="Artikelname bearbeiten"
                formComponent={
                    <QeeSingleFieldEditor
                        fieldType={'TextField'}
                        title={'Artikelname'}
                        label={'Name'}
                        placeholder={'Geben Sie den Namen des Artikels ein'}
                        vl={ordData.art_name}
                        fctOnChanges={(vl) => handleChangesDialog({ art_name: vl.text })}
                    />
                }
                fctOnSubmit={handleSave}
            />
            <QeeSlideDialog
                id="dlgArticleID"
                title="Artikel-ID bearbeiten"
                formComponent={
                    <QeeSingleFieldEditor
                        fieldType={'TextField'}
                        title={'Artikel-ID'}
                        label={'ID'}
                        placeholder={'Geben Sie die ID des Artikels ein'}
                        vl={ordData.art_id}
                        fctOnChanges={(vl) => handleChangesDialog({ art_id: vl.text })}
                    />
                }
                fctOnSubmit={handleSave}
            />

            {/* Article Details Dialog */}
            <QeeSlideDialog
                id="dlgArticle"
                title="Artikel Details"
                onClose={() => setOpenQeeDialog({ open: false, id: 'dlgArticle' })}
                formComponent={
                    <div style={{ paddingTop: 50 }}>
                        <QeeArticleForm artId={selectedArtId} />
                    </div>
                }
                hideDialogActions={true}
                submitDisabled={true}
            />
			
            {/* Offer Dialog */}
            <QeeSlideDialog
                id="dlgOffer"
                title="Angebot"
                onClose={() => setOpenQeeDialog({ open: false, id: 'dlgOffer' })}
                formComponent={
                    <div style={{ paddingTop: 50 }}>
                        <OfferForm 
							ord={ordData} 
							sac={sac}
							fctOnHasErrors={handleHasErrorsDlgOffer}
							fctOnChanges={handleChangesDlgOffer}
						/>
                    </div>
                }
				submitDisabled={hasErrorsDlgOffer}
				fctOnSubmit={handleSubmitDlgOffer}				
            />			
			
            {/* Contract Dialog */}
            <QeeSlideDialog
                id="dlgContract"
                title="Vertrag"
                onClose={() => setOpenQeeDialog({ open: false, id: 'dlgContract' })}
                formComponent={
                    <div style={{ paddingTop: 50 }}>
                        <ContractForm 
							ord={ordData} 
							sac={sac}
							fctOnHasErrors={handleHasErrorsDlgContract}
							fctOnChanges={handleChangesDlgContract}
						/>
                    </div>
                }
				submitDisabled={hasErrorsDlgContract}
				fctOnSubmit={handleSubmitDlgContract}				
            />						
        </div>
    );
};

export default OrdForm;
